import React from 'react';
import { Box, Typography } from '@mui/material';
// From utils, NOT from the hook: components that mock useCreditBalances in
// tests replace that module wholesale, which used to take these with it.
import {
  USAGE_ACTIONS, USAGE_ACTION_LABELS, usageFor, generationCountLabel,
} from '../utils/creditUsage';

/**
 * Credits used this cycle, broken down by what they were spent on.
 *
 * ── WHAT THIS REPLACES, AND WHY ─────────────────────────────────────────────
 * The Credits page used to show three cards — SEO Content, AI Images, Keyword
 * Lists — each printing the whole pooled balance. With 634 credits it read
 * "634" three times, which invites the reader to add them up and conclude they
 * have 1,902. The same illusion in the Settings tab was reported separately.
 *
 * The three cards also each showed a "plan usage this cycle" progress bar, and
 * all three were fed from the SAME object, so the identical bar was drawn three
 * times regardless of what had actually been generated.
 *
 * The fix is to stop splitting the BALANCE (there is only one, and the card
 * above already states it) and start splitting the SPEND, which genuinely does
 * divide by action. That number is real, it adds up to a meaningful total, and
 * it answers the question the three cards were pretending to.
 *
 * ── DESIGN NOTES ────────────────────────────────────────────────────────────
 * Both rows carry equal visual weight — same type size, same type colour —
 * because the relative sizes should come from the DATA, not from the styling.
 * The colour on each row is an IDENTIFIER tying it to its share of the bar, not
 * emphasis. Content is listed first as the costliest and most-used action, then
 * images. Keyword lists were a third row until they became free: nothing is
 * spent on them, so they have no share of the spend to show (USAGE_ACTIONS).
 *
 * ── THE BAR ─────────────────────────────────────────────────────────────────
 * One bar, segmented by action, whose full width is the credits spent this
 * cycle. It answers "what did the credits go on?" — the one question the
 * numbers beside it do not answer at a glance.
 *
 * It replaces three separate bars, one per row, each scaled against the largest
 * row. That made the biggest action a full bar whatever it cost and measured
 * the others against it, so it encoded RANKING and nothing else — which the
 * numbers already gave. QA asked what its logic was; there wasn't one worth
 * keeping.
 *
 * Scaled against the SPEND, not the allotment: against an allotment a light
 * month is a near-empty bar answering "how much is left", which the headline
 * already answers in words, and the split — the thing this exists to show —
 * would compress into invisibility.
 *
 * No minimum segment width. A 3% action gets a 3% band; padding small slices up
 * to something visible would make the bar lie about the only thing it shows. An
 * action costing zero renders no segment at all rather than a hairline.
 *
 * Deliberately absent: remaining balances (stated by the card above — repeating
 * them is exactly the duplication this replaces) and the Buy Credits buttons,
 * which were removed by request: a usage summary is not a sales unit.
 */

const TEXT = '#1A1A1A';
const TEXT_MUTED = '#8A8F85';
const CARD_BORDER = '#E5E7E0';
const TRACK = '#F0F0EA';

/**
 * One brand colour per action, shared by the bar segment and the swatch on its
 * row so the two are read as one thing. Coral and turquoise are the CMS's
 * existing accents; they are chosen to be distinguishable from each other
 * rather than to rank the rows.
 */
const ACTION_COLOURS = {
  content: '#E55432',   // coral — the primary accent across the product
  images: '#1C7C73',    // turquoise — the teal used for credits and links
};

export default function CreditUsageCard({ usage, total, cycleResetsAt, loading = false }) {
  const rows = USAGE_ACTIONS.map((action) => ({
    action,
    label: USAGE_ACTION_LABELS[action],
    colour: ACTION_COLOURS[action],
    ...usageFor(usage, action),
  }));

  const usedCredits = Number(usage?.totalCredits) || 0;
  // The bar divides the spend, so it is measured against what the ROWS add up
  // to rather than against usage.totalCredits — those can disagree if the
  // server adds a fourth action before this list knows about it, and a set of
  // shares that does not fill its own bar is worse than one that does.
  const barTotal = rows.reduce((sum, r) => sum + r.credits, 0);
  // Zero credits is a real state (nothing generated yet), and dividing by it
  // must not produce NaN — hence the guard rather than a bare division.
  const segments = barTotal > 0
    ? rows.filter((r) => r.credits > 0).map((r) => ({ ...r, share: (r.credits / barTotal) * 100 }))
    : [];
  const nothingYet = usedCredits === 0;

  const resetLabel = cycleResetsAt
    ? new Date(cycleResetsAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : null;

  return (
    <Box
      sx={{
        bgcolor: '#FFFFFF',
        border: `1px solid ${CARD_BORDER}`,
        borderRadius: '16px',
        p: { xs: '20px 24px', md: '22px 28px' },
        boxShadow: '0px 4px 4px 0px rgba(0,0,0,0.25)',
      }}
    >
      <Typography
        sx={{
          fontFamily: 'Inter, sans-serif',
          fontWeight: 700,
          fontSize: 11,
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
          color: TEXT_MUTED,
          mb: 0.5,
        }}
      >
        Credits Used This Cycle
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
        <Typography
          sx={{
            fontFamily: 'Manrope, sans-serif', fontWeight: 800, fontSize: 34, lineHeight: 1, color: TEXT,
          }}
        >
          {loading ? '—' : usedCredits.toLocaleString()}
        </Typography>
        {/* The denominator is the balance, so the headline reads as a fraction
            of something the user can see rather than a bare number. */}
        {!loading && typeof total === 'number' && total > 0 && (
          <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: 14, color: TEXT_MUTED }}>
            of {(usedCredits + total).toLocaleString()} total credits
          </Typography>
        )}
      </Box>

      {/* ── Where the cycle's credits went ───────────────────────────────────
          Segments in row order, so the bar reads left-to-right in the same
          sequence as the list beneath it. Rendered only when something has
          been spent: an empty track under a zero headline states nothing the
          "Nothing generated yet" line does not say better. */}
      {!loading && segments.length > 0 && (
        <Box
          role="progressbar"
          aria-label={`Credits used this cycle by activity: ${
            segments.map((s) => `${s.label} ${Math.round(s.share)}%`).join(', ')}`}
          aria-valuenow={usedCredits}
          aria-valuemin={0}
          aria-valuemax={usedCredits}
          sx={{
            display: 'flex', height: 10, borderRadius: 5, overflow: 'hidden',
            bgcolor: TRACK, mb: 2,
          }}
        >
          {segments.map((s) => (
            <Box
              key={s.action}
              data-action={s.action}
              // No minimum width: a small share must look small. flexShrink is
              // pinned off so rounding cannot let the segments fall short of
              // filling the track.
              sx={{ width: `${s.share}%`, flexShrink: 0, bgcolor: s.colour }}
            />
          ))}
        </Box>
      )}

      {/* Column headers, so the two numbers on each row are not ambiguous. */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr auto auto',
          columnGap: 2,
          pb: 0.75,
          borderBottom: `1px solid ${CARD_BORDER}`,
        }}
      >
        {['ACTIVITY', 'GENERATED', 'CREDITS'].map((h, i) => (
          <Typography
            key={h}
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 700,
              fontSize: 10,
              letterSpacing: '0.08em',
              color: TEXT_MUTED,
              textAlign: i === 0 ? 'left' : 'right',
              minWidth: i === 0 ? 0 : 68,
            }}
          >
            {h}
          </Typography>
        ))}
      </Box>

      {rows.map((row) => (
        <Box key={row.action} sx={{ pt: 1.5 }}>
          <Box
            sx={{
              display: 'grid', gridTemplateColumns: '1fr auto auto', columnGap: 2, alignItems: 'baseline',
            }}
          >
            <Typography
              sx={{
                fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 14, color: TEXT,
                display: 'flex', alignItems: 'center', gap: 1,
              }}
            >
              {/* Ties the row to its band in the bar above. Decorative — the
                  row already names the activity in words. */}
              <Box
                aria-hidden="true"
                data-swatch={row.action}
                component="span"
                sx={{
                  width: 10, height: 10, borderRadius: '3px', flexShrink: 0,
                  bgcolor: row.colour,
                  // A faint ring keeps the swatch's edge crisp on the white card;
                  // applied to every row so none is treated differently.
                  boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.08)',
                }}
              />
              {row.label}
            </Typography>
            <Typography
              sx={{
                fontFamily: 'Inter, sans-serif', fontSize: 13, color: TEXT_MUTED, textAlign: 'right', minWidth: 68,
              }}
            >
              {generationCountLabel(row.action, row.generations)}
            </Typography>
            <Typography
              sx={{
                fontFamily: 'Inter, sans-serif', fontWeight: 700, fontSize: 14, color: TEXT, textAlign: 'right', minWidth: 68,
              }}
            >
              {row.credits.toLocaleString()}
            </Typography>
          </Box>
        </Box>
      ))}

      {nothingYet && !loading && (
        <Typography
          sx={{ fontFamily: 'Inter, sans-serif', fontSize: 13, color: TEXT_MUTED, mt: 2 }}
        >
          Nothing generated yet this cycle.
        </Typography>
      )}

      {resetLabel && (
        <Typography
          sx={{ fontFamily: 'Inter, sans-serif', fontSize: 12, color: TEXT_MUTED, mt: 2 }}
        >
          Cycle resets {resetLabel}
        </Typography>
      )}
    </Box>
  );
}
